<template>
  <div class="sales page page-with-table p-2">
    <div class="card mb-2 p-2">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <h4 class="mb-0 mr-4">
            {{ $t('Batch Lot Up V2 for Sale') }}
            <router-link v-if="sale" :to="{ name: 'sales.lots', params: { id: sale.uuid }}">
              {{ sale.sale_no }}
            </router-link>
            <live-sale-ref v-if="sale && sale.live_reference" :href="sale.live_url" :label="sale.live_reference"/>
          </h4>
        </div>
        <div class="d-flex align-items-center">
          <input-widget :model="model" attribute="uuid" value-field="uuid" display-field="${item.title}"
                        type="select2" :items="customSequences" :label="false" :options="{placeholder: $t('Sequence')}"
                        style="width: 15em" class="mr-2" @select2Select="onSequenceSelect"/>
          <submit-button :show-loader="isInProgress" variant="primary" @click="onApplyClick" :disabled="!sortingParams">
            {{ $t('Apply') }}
          </submit-button>
        </div>
        <div class="d-flex align-items-center">
          <submit-button :show-loader="isInProgress" class="mr-2" @click="onToggleColumnsClick"
                         v-b-tooltip.hover.bottomleft="$t('Toggle Columns')" variant="secondary">
            <font-awesome-icon icon="columns" class="icon"/>
          </submit-button>
          <submit-button :show-loader="isInProgress" class="ml-auto" variant="primary" @click="onSaveClick">
            {{ $t('Save') }}
          </submit-button>
        </div>
      </div>
    </div>

    <div class="content-wrapper mb-2 bg-white overflow-auto" style="height: 87% !important">
      <spinner :show="loading" class="mt-2"/>
      <div v-show="!loading" class="h-100">
        <div class="flex-container flex-container-style fixed-height h-100" style="">
          <table class="table table-sm items-table">
            <thead>
            <tr>
              <th></th>
              <th class="lot-no-col">{{ $t('Lot No') }}</th>
              <th class="stock-no-col">{{ $t('Stock No') }}</th>
              <th class="department-col">{{ $t('Department') }}</th>
              <th class="category-col">{{ $t('Category') }}</th>
              <th class="dynamic-col" v-for="df in dynamicColumns" :key="`dynamic-col-th=${df.df_key}`">{{ df.label }}
              </th>
            </tr>
            </thead>
            <draggable :options="{'disabled': isInProgress}" @change="onItemDrag"
                       :list="loopItems" tag="tbody" handle=".handle">
              <tr v-for="item in loopItems" :key="`lot-row-${item.uuid}`">
                <td class="text-right">
                  <font-awesome-icon class="icon handle hover-row-resize" icon="bars"/>
                </td>
                <td>
                  <a href="javascript:void(0)" @click="onLotNoClick(item)">{{ item.lot_no }}</a>
                </td>
                <td>{{ item.stock_no }}</td>
                <td>{{ item.department }}</td>
                <td>{{ item.category }}</td>
                <td v-for="df in dynamicColumns" :key="`dynamic-col-td=${df.df_key}`">
                  <div>
                    {{
                      $options.filters.asDynamicField(
                        $options.filters.attributeByMasterLanguage(item.dynamic_fields, df.df_key),
                        df.type,
                        df.choices,
                        true
                      ) | striptags | truncate(200)
                    }}
                  </div>
                </td>
              </tr>
            </draggable>

          </table>
        </div>
      </div>

    </div>

    <div class="bg-white p-2" style="min-height: 3.2em">
      <paginator :pagination="pagination" v-if="!loading && allLoopItems && allLoopItems.length > 100"
                 @pageChange="onPageChange"/>
    </div>

    <BLUV2ToggleColumnsModal @hidden="onToggleColumnsHidden"/>
    <BLUV2LotEditModal @submit="onLotEditSubmit"/>

  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "../../../core/components/common/Spinner";
  import SalesService from "./../SalesService";
  import LiveSaleRef from "@/core/components/common/LiveSaleRef";
  import {
    WS_BATCH_LOT_UP_PROGRESS,
  } from "@/modules/constants";
  import SubmitButton from "@/core/components/common/SubmitButton";
  import {Formatter} from "@/core/services/formatter.service";
  import Paginator from "@/core/components/paginator/Paginator";
  import {chunk, cloneDeep} from "lodash";
  import BatchLotUpV2Service from "@/modules/sales/batch-lot-up-v2/BatchLotUpV2Service";
  import BLUV2ToggleColumnsModal from "@/modules/sales/batch-lot-up-v2/BLUV2ToggleColumnsModal";
  import draggable from "vuedraggable";
  import ClsService from "@/modules/setup/cls/ClsService";
  import BatchLotUpV2Model from "@/modules/sales/batch-lot-up-v2/BatchLotUpV2.model";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import BLUV2LotEditModal from "@/modules/sales/batch-lot-up-v2/BLUV2LotEditModal";

  const {mapActions, mapState} = createNamespacedHelpers('sales/batchLotUpV2');
  const {mapState: globalMapState, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');
  const {mapState: mapStateDepartment} = createNamespacedHelpers('setup/department');

  export default {
    name: "BatchLotUp",
    components: {
      BLUV2LotEditModal,
      InputWidget, BLUV2ToggleColumnsModal, Paginator, SubmitButton, LiveSaleRef, Spinner, draggable
    },
    data() {
      return {
        progress: null,
        localLoading: false,
        loopItems: [],
        allLoopItems: [],
        dynamicColumns: [],
        pagination: {
          limit: 200,
          page: 1,
          total: 0,
        },
        customSequences: [],
        model: new BatchLotUpV2Model(),
        deptMap: {},
        catMap: {},
        sortingParams: null,
        dfSortingOptions: {}
      }
    },
    computed: {
      ...mapState({
        vuexLoading: state => state.batchLotUpV2Data.loading,
        sale: state => state.batchLotUpV2Data.sale,
        items: state => state.batchLotUpV2Data.items,
        reservedLotNos: state => state.batchLotUpV2Data.reservedLotNos,
      }),
      ...globalMapState({
        enableInventoryQuantity: state => state.enableInventoryQuantity,
        uniqueDynamicFields: state => state.uniqueDynamicFields,
      }),
      ...mapStateDepartment(['departments']),
      isInProgress() {
        return this.progress !== null
      },
      loading() {
        return this.vuexLoading || this.localLoading
      }
    },
    methods: {
      ...mapActionsGlobal(['addProgress', 'removeProgress', 'updateProgressValue']),
      ...mapActions(['initBatchLotUpV2Data', 'clearData', 'showToggleColumnsModal', 'showLotEditModal']),
      async addProgressIfNotExist() {
        if (!this.progress) {
          this.progress = await this.addProgress(
            this.$i18n.t("Batch lot up for {sale_no}", {sale_no: this.sale.sale_no})
          );
        }
        return true
      },
      removeProgressIfExist() {
        if (this.progress) {
          this.removeProgress(this.progress.uuid)
          this.progress = null;
        }
      },
      async onProgressNotificationReceive(data) {
        await this.addProgressIfNotExist()
        this.updateProgressValue({
          progressValue: parseInt(Math.round(data.successful / data.total * 100)),
          uuid: this.progress.uuid
        });

        if (data.is_finished) {
          if (!data.errors) {
            this.$toast('success', this.$i18n.t("Batch lot up has successfully finished"));
          }
          this.removeProgressIfExist();
        }
        if (data.errors) {
          if (data.errors.non_field_errors) {
            this.$errorDialog(data.errors.non_field_errors[0])
          } else if (data.errors.errors) {
            const listItemsArr = [];
            data.errors.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong while running batch lot up. Please, try again later."))
          }
        }
      },
      async onSaveClick() {
        let requestData = [];
        let raiseError = false
        this.allLoopItems.forEach((item) => {
          if (!item.lot_no) {
            raiseError = true
          }
          requestData.push({uuid: item.uuid, lot_no: item.lot_no})
        });

        if (raiseError) {
          this.$errorDialog(this.$i18n.t('Please, apply sequence and try saving again.'))
          return
        }

        let response = await SalesService.batchLotUp(this.sale.sale_no, requestData);
        if (response.success) {
          await this.addProgressIfNotExist();
          this.$toast('success', this.$i18n.t('Batch lot up has successfully started'))
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0])
        }
      },
      onToggleColumnsClick() {
        this.showToggleColumnsModal()
      },
      onApplyClick() {
        // [
        //   {key: "dynamic_field.title", type: "asc", position: 1, top_data: ['candidate', 'family']},
        //   {key: "category", type: "asc", position: 2},
        //   {key: "dynamic_field.description", type: "desc", position: 3, top_data: ['competition']},
        //   // { key: "dynamic_field.date_of_birth", type: "asc", position: 3, top_data: ["2022-05-21", "2023-01-01"] },
        //   // { key: "dynamic_field.description", type: "desc", position: 4 }
        // ]

        console.log(this.sortingParams);
        console.log(this.dfSortingOptions);
        console.log(this.allLoopItems);
        console.log('start sorting');

        this.localLoading = true

        let tmpItemsForSorting = cloneDeep(this.allLoopItems)

        const dfKeysToNormalize = []
        for (let param of this.sortingParams) {
          if (param.key.startsWith('dynamic_field.')) {
            dfKeysToNormalize.push(param.key.split('.')[1])
          }
        }

        for (let lot of tmpItemsForSorting) {
          for (let dfKey of dfKeysToNormalize) {
            let tmpVal = lot.dynamic_fields.en[dfKey]

            if (tmpVal !== undefined && tmpVal !== null) {
              if (this.dfSortingOptions[dfKey] === 'number') {
                lot.dynamic_fields.en[dfKey] = parseFloat(tmpVal)
              } else if (this.dfSortingOptions[dfKey] === 'boolean') {
                if (typeof tmpVal == "boolean") {
                  lot.dynamic_fields.en[dfKey] = tmpVal
                } else {
                  lot.dynamic_fields.en[dfKey] = tmpVal.trim().toLowerCase() === 'true'
                }
              } else {
                lot.dynamic_fields.en[dfKey] = `${tmpVal}`
              }
            }
          }
        }

        let sortedItems = BatchLotUpV2Service.sortItems(tmpItemsForSorting, this.sortingParams, this.dfSortingOptions)

        console.log('finish sorting');
        console.log(sortedItems);

        this.calculateLotNos(sortedItems)

        this.allLoopItems = sortedItems
        this.pagination.page = 1
        this.calculateCurrentPage()
        this.$nextTick(() => {
          this.localLoading = false
        })
      },
      calculateLotNos(sortedItems) {
        let counter = 0
        for (let item of sortedItems) {
          let nextCounter = counter;
          while (true) {
            nextCounter++
            if (!this.reservedLotNos.includes(nextCounter)) {
              counter = nextCounter;
              break;
            }
          }
          item.lot_no = counter
        }
      },
      calculateCurrentPage() {
        this.pagination.total = this.allLoopItems.length
        this.loopItems = chunk(this.allLoopItems, this.pagination.limit)[this.pagination.page - 1]
      },
      onPageChange(page) {
        this.pagination.page = page;
        this.calculateCurrentPage()
      },
      async initData() {
        // initializing lots data
        let response = await this.initBatchLotUpV2Data(this.$route.params.sale_no);
        if (response.success) {
          const simplifiedObjects = []
          for (let item of this.items) {

            let tmpDynamicFields = {}
            for (let lang in item.inventory.dynamic_fields) {
              tmpDynamicFields[lang] = {}
              for (let dfKey in item.inventory.dynamic_fields[lang]) {
                if (typeof item.inventory.dynamic_fields[lang][dfKey] === 'string') {
                  tmpDynamicFields[lang][dfKey] = Formatter.stripTags(item.inventory.dynamic_fields[lang][dfKey])
                } else {
                  tmpDynamicFields[lang][dfKey] = item.inventory.dynamic_fields[lang][dfKey]
                }
              }
            }

            simplifiedObjects.push({
              stock_no: item.inventory.stock_no,
              images: item.inventory.images,
              dynamic_fields: tmpDynamicFields,
              lot_no: item.lot_no ? parseInt(item.lot_no) : null,
              uuid: item.uuid,
              department: Formatter.getByMasterLanguage(item.inventory.department.name),
              category: Formatter.getByMasterLanguage(item.inventory.category.name),
            })
          }

          this.setDynamicColumns()

          this.allLoopItems = cloneDeep(simplifiedObjects)
          this.calculateCurrentPage()
        }

        // initializing cls data
        response = await ClsService.getCustomLotSequences({limit: 1000})
        if (response.success) {
          this.customSequences = response.body.results
        }

        let mapCategories = (categories) => {
          if (categories && categories.length > 0) {
            for (let cat of categories) {
              this.catMap[cat.uuid] = this.$options.filters.masterLanguage(cat.name)
              mapCategories(cat.children)
            }
          }
        }

        // initializing departments/categories data
        for (let dept of this.departments.rows) {
          this.deptMap[dept.uuid] = this.$options.filters.masterLanguage(dept.name)
          mapCategories(dept.categories)
        }

        return true
      },
      onToggleColumnsHidden() {
        this.setDynamicColumns()
      },
      setDynamicColumns() {
        let savedValues = BatchLotUpV2Service.getColumns()
        let tmpArr = []

        for (let dfKey of savedValues) {
          let df = this.uniqueDynamicFields[dfKey]
          if (df) {
            tmpArr.push({
              choices: df.rules && df.rules.choices ? df.rules.choices : [],
              df_key: dfKey,
              type: df.field_class_name === 'MainField' ? 'text' : df.type,
              label: df.label
            })
          }
        }
        this.dynamicColumns = tmpArr
      },
      onItemDrag() {
        this.localLoading = true
        let startIndex = this.pagination.limit * (this.pagination.page - 1)
        for (let item of this.loopItems) {
          this.allLoopItems[startIndex] = cloneDeep(item)
          startIndex++;
        }

        this.calculateLotNos(this.allLoopItems)
        this.calculateCurrentPage()
        this.$nextTick(() => {
          this.localLoading = false
        })
      },
      onSequenceSelect(item) {
        this.sortingParams = null

        if (!item.data || item.data.length === 0) {
          return false
        }

        let position = 1
        let tmpSortParams = []

        for (let item of item.data) {
          // console.log(item);
          let myObj = {
            key: item.type === `dynamic_field` ? `dynamic_field.${item.dynamic_field_key}` : item.type,
            type: item.sorting === 'custom' ? "asc" : item.sorting,
            position: position++,
            top_data: []
          }

          if (item.customSortingOptions && item.customSortingOptions.length > 0) {
            for (let innerObj of item.customSortingOptions) {
              if (item.type === 'department') {
                myObj.top_data.push(this.deptMap[innerObj.department_uuid])
              } else if (item.type === 'category') {
                myObj.top_data.push(this.catMap[innerObj.category_uuid])
              } else {
                myObj.top_data.push(innerObj.dropdown_option_value)
              }
            }
          }

          tmpSortParams.push(myObj)
        }

        this.sortingParams = cloneDeep(tmpSortParams)
      },
      onLotNoClick(item) {
        const maxValue = this.allLoopItems.reduce((max, obj) => (obj.lot_no > max ? obj.lot_no : max), this.allLoopItems[0].lot_no);
        this.showLotEditModal({object: cloneDeep(item), maxLotNo: maxValue})
      },
      onLotEditSubmit({item, newLotNo}) {
        this.localLoading = true

        let fromIndex = this.allLoopItems.findIndex(i => i.uuid === item.uuid)
        let toIndex = this.allLoopItems.findIndex(i => i.lot_no === newLotNo)

        if (fromIndex < 0 || fromIndex >= this.allLoopItems.length || toIndex < 0 || toIndex >= this.allLoopItems.length) {
          console.error("Invalid indices");
          return false;
        }

        // Remove the element from the original position
        const [movedElement] = this.allLoopItems.splice(fromIndex, 1);

        // Insert the element at the new position
        this.allLoopItems.splice(toIndex, 0, movedElement);

        this.calculateLotNos(this.allLoopItems)

        this.calculateCurrentPage()
        this.$nextTick(() => {
          this.localLoading = false
        })
      },
      setDynamicFieldSortingOptions() {
        for (let key in this.uniqueDynamicFields) {
          const df = this.uniqueDynamicFields[key]
          if (df.type === 'number') {
            this.dfSortingOptions[key] = 'number'
          } else if (df.type === 'checkbox') {
            this.dfSortingOptions[key] = 'boolean'
          } else if (df.type === 'date') {
            this.dfSortingOptions[key] = 'date'
          } else {
            this.dfSortingOptions[key] = 'string'
          }
        }
      }
    },
    async beforeMount() {
      this.localLoading = true
      await this.initData()
      this.setDynamicFieldSortingOptions()
      this.localLoading = false
    },
    mounted() {
      this.$ws.listen(`sales.${WS_BATCH_LOT_UP_PROGRESS}`, this.onProgressNotificationReceive);
    },
    beforeDestroy() {
      this.clearData()
      this.removeProgressIfExist();
      this.$ws.remove(`sales.${WS_BATCH_LOT_UP_PROGRESS}`);
      this.pagination = {
        limit: 10,
        page: 1,
        total: 0,
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./../scss/variables";

  .items-table {

    th {
      position: sticky;
      top: -0.5px;
      background-color: white;
    }

    .lot-no-col {
      min-width: 5em;
    }

    .stock-no-col {
      min-width: 8em;
    }

    .dynamic-col {
      min-width: 8em;
    }

    .department-col {
      min-width: 10em;
    }

    .category-col {
      min-width: 10em;
    }
  }

  /deep/ .form-group {
    margin-bottom: 0;
  }
</style>

